<template>
  <section v-if="result?.length" class="px-4 md:px-0">
    <div class="flex flex-col justify-center">
      <ProductListSection
        :title="title"
        class="mx-auto max-w-7xl"
        :try-on-available-frames="tryOnAvailableFrames"
        :products="result"
        :active-image="activeImage"
        :category="category"
      />

      <ZnButton
        class="border-teal-dark mx-auto hidden md:block"
        type="secondary"
      >
        <nuxt-link
          :to="{
            path: `/products/list${category ? `/${category}` : ''}`,
            query: { activeImage: activeImage },
          }"
          >Shop all</nuxt-link
        >
      </ZnButton>
      <div class="flex items-center justify-between md:hidden">
        <nuxt-link
          :to="{
            path: `/products/list${category ? `/${category}` : ''}`,
            query: { activeImage: activeImage },
          }"
          class="underline"
          >Shop all</nuxt-link
        >
        <div class="flex items-center gap-2">
          <div
            class="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-gray-100"
            @click="scrollLeft"
            :class="{
              'pointer-events-none opacity-50': atStart,
            }"
          >
            <ZenniIconsIconChevronRight
              class="text-grays-black h-7 w-7 rotate-180"
            />
          </div>
          <div
            class="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-gray-100"
            @click="scrollRight"
            :class="{
              'pointer-events-none opacity-50': atEnd,
            }"
          >
            <ZenniIconsIconChevronRight class="text-grays-black h-7 w-7" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { productAttributesToRetrieve } from '@/utils/normalize/algoliaSearchNormalize'
import {
  ref,
  useLazyAsyncData,
  watchEffect,
  onMounted,
  onUnmounted,
} from '#imports'
import { useGuestStore } from '~/store/guest'
import { useFittingbox } from '~/composables/useFittingbox'
import { formatSkuId } from '~/utils/skuIdGetters'
import { useBestSellerProducts } from '~/composables/useBestSellerProducts'
import type { ZnAlgoliaProduct } from '@/types/algolia-results/algolia-types'

const { getBestSellerProducts, result } = useBestSellerProducts()
const { getAvailableFrames } = useFittingbox()

// TODO: We should get the available balance from the gift store
const { guest } = useGuestStore()

const props = withDefaults(
  defineProps<{
    title: string
    filter?: string
    category?: string
    activeImage?: 'tinted'
    searchQuery?: string
  }>(),
  {
    filter: '',
    category: undefined,
    activeImage: undefined,
    searchQuery: '',
  },
)

// TODO: We should reconfigure the filters when we have more data about how to filter the popular products
const maxHits = 4

useLazyAsyncData('best-sellers', async () => {
  await getBestSellerProducts(maxHits, productAttributesToRetrieve)
})

const tryOnAvailableFrames = ref<string[]>([])

const refreshTryOnList = async () => {
  const availableFrames = await getAvailableFrames(
    result.value?.map((hit: ZnAlgoliaProduct) => {
      return formatSkuId(hit.sku)
    }),
  )
  tryOnAvailableFrames.value = availableFrames
}

watchEffect(async () => {
  if (result.value?.length && import.meta.client) {
    await refreshTryOnList()
  }
})

const atStart = ref(true)
const atEnd = ref(false)

const updateScrollPosition = () => {
  const container = document.querySelector(`.${props.category}`)
  if (container) {
    atStart.value = container.scrollLeft === 0
    atEnd.value =
      Math.abs(
        container.scrollLeft - (container.scrollWidth - container.clientWidth),
      ) < 2 // allow for rounding
  }
}

const scrollLeft = () => {
  const container = document.querySelector(`.${props.category}`)

  if (container) {
    container.scrollBy({
      left: -300,
      behavior: 'smooth',
    })
  }
}

const scrollRight = () => {
  const container = document.querySelector(`.${props.category}`)
  if (container) {
    container.scrollBy({
      left: 300,
      behavior: 'smooth',
    })
  }
}

onMounted(() => {
  const container = document.querySelector(`.${props.category}`)
  if (container) {
    container.addEventListener('scroll', updateScrollPosition)
    updateScrollPosition()
  }
})

onUnmounted(() => {
  const container = document.querySelector(`.${props.category}`)
  if (container) {
    container.removeEventListener('scroll', updateScrollPosition)
  }
})
</script>
